import { z } from "zod";
import { defineStepConfig } from "@erp/workflows";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Match Invoice Line Items Step
 *
 * Performs 2-way matching between invoice line items and PO line items.
 *
 * Uses:
 * - Embedding-based description matching
 * - Quantity and price comparison
 * - AI arbitration for ambiguous matches
 * - Stores entity associations for matched items
 *
 * Note: This is a backend-only step that requires:
 * - AI model access for arbitration
 * - Database write access for EntityAssociations
 */
export const matchInvoiceLineItemsStep = defineStepConfig({
  type: "match_invoice_line_items",
  name: "Match Invoice Line Items",
  description:
    "Match invoice line items to purchase order line items with AI-enhanced confidence scoring",
  category: "Transformation",
  libraryModule: "AccountsPayable",
  inputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE).describe(
      "AP Invoice to reconcile",
    ),
    /** @deprecated Use purchaseOrders instead for multiple PO support */
    purchaseOrder: entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER)
      .describe(
        "@deprecated Use purchaseOrders instead. Single Internal Purchase Order to match against.",
      )
      .optional(),
    purchaseOrders: z
      .array(entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER))
      .optional()
      .describe("Array of Internal Purchase Orders to match against"),
    salesOrders: z
      .array(entityOfType(SystemEntity.AR_SALES_ORDER))
      .optional()
      .describe("Optional array of Sales Orders to match against"),
    transferOrders: z
      .array(entityOfType(SystemEntity.IM_TRANSFER_ORDER))
      .optional()
      .describe("Optional array of Transfer Orders to match against"),
  }),

  outputSchema: z.object({
    lowestConfidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Lowest confidence score among all line item matches"),
    // TODO(GEN-1861): Temporary Famous-status gate output. Remove when proper sync-gating lands.
    // https://linear.app/gently-ai/issue/GEN-1861/cleanup-remove-hack-in-matchinvoicelineitemshandler
    famousStatusNotReady: z
      .boolean()
      .describe(
        "True when matched Famous SO/PO references are not yet in a ready status (SO >= Invoiced; PO = FullyReceived). Deterministic routing flag for snooze-and-rerun.",
      ),
    unreadyReferences: z
      .array(
        z.object({
          type: z.enum(["SO", "PO"]),
          id: z.string(),
          currentStatus: z.string().nullable(),
        }),
      )
      .describe(
        "References whose Famous status is not yet ready. Empty when famousStatusNotReady is false.",
      ),
  }),

  configSchema: z.object({
    matchConfidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.7)
      .describe("Minimum confidence to auto-accept a match"),
    enableFuzzySearch: z
      .boolean()
      .prefault(true)
      .describe("Enable AI-powered fuzzy matching when no references"),
    agentFallbackConfidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.7)
      .describe(
        "When the reconciliation easy-path produces a lowest confidence below this threshold, fall back to the AI matching agent",
      ),
    priceTolerancePercent: z
      .number()
      .prefault(1)
      .describe("Price variance tolerance for matching (percentage)"),
    quantityTolerancePercent: z
      .number()
      .prefault(5)
      .describe("Quantity variance tolerance for matching (percentage)"),
    requiredInvoiceFields: z
      .object({
        supplier: z
          .boolean()
          .prefault(false)
          .describe("Require the invoice to have a supplier before matching"),
        referenceIds: z
          .boolean()
          .prefault(false)
          .describe(
            "Require the invoice to have at least one reference ID before matching",
          ),
        totalAmount: z
          .boolean()
          .prefault(false)
          .describe(
            "Require the invoice to have a total amount before matching",
          ),
      })
      .prefault({})
      .describe(
        "Optional invoice prerequisites that must be present before line-item matching runs",
      ),
  }),

  emits: [] as const,
});
